import { useState, useCallback } from "react";
import { Download } from "lucide-react";
import { useRemoteTable } from "@/features/categories/components/category-views/shared/useRemoteTable";
import { RemoteTableLayout } from "@/features/categories/components/category-views/shared/RemoteTableLayout";
import { FiltersButton } from "@/features/categories/components/category-views/shared";
import { Button } from "@/shared/ui/button";
import type {
  JobPosting,
  JobPostingQueryRequest,
} from "@/services/api/job-postings";
import type { VisibilityState } from "@tanstack/react-table";
import { useJobPostings } from "../queries/useJobPostings";
import { useJobPostingSearchParams } from "../hooks/useJobPostingSearchParams";
import { useJobPostingColumns } from "./columns";
import { AVAILABLE_COLUMNS } from "./columnConfig";
import { JobPostingsTable } from "./JobPostingsTable";
import { JobPostingsFilters } from "./JobPostingsFilters";
import { JobPostingDetailDialog } from "./JobPostingDetailDialog";
import { ImportJobPostingsDialog } from "@/features/admin/panels/job-postings/ImportJobPostingsDialog";

const ALLOWED_PAGE_SIZES = [30, 60, 90] as const;

export function JobPostingsPage() {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedPosting, setSelectedPosting] = useState<JobPosting | null>(
    null,
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const {
    pagination,
    sorting,
    filters,
    queryRequest,
    setPagination,
    setSorting,
    setFilters,
  } = useJobPostingSearchParams();

  const { data, isLoading, error } = useJobPostings(queryRequest);

  const postings = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;
  const currentPage = pagination.pageIndex + 1;
  const pageSize = pagination.pageSize;

  const handleOpenDetail = useCallback((posting: JobPosting) => {
    setSelectedPosting(posting);
  }, []);

  const columns = useJobPostingColumns({ onTitleClick: handleOpenDetail });

  const table = useRemoteTable<JobPosting>({
    data: postings,
    columns,
    pageCount: totalPages,
    pagination,
    sorting,
    columnVisibility,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
  });

  const handleFilterChange = useCallback(
    (
      key: keyof JobPostingQueryRequest,
      value: string | boolean | string[] | undefined,
    ) => {
      const newFilters = { ...filters, [key]: value };

      const normalizedFilters: Partial<JobPostingQueryRequest> = {};
      Object.entries(newFilters).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") {
          if (Array.isArray(v) && v.length === 0) return;
          normalizedFilters[k as keyof JobPostingQueryRequest] = v as never;
        }
      });

      setFilters(normalizedFilters);
    },
    [filters, setFilters],
  );

  const handleClearFilters = useCallback(() => {
    setFilters({});
  }, [setFilters]);

  const handlePageChange = useCallback(
    (page: number) => {
      setPagination((prev) => ({ ...prev, pageIndex: page - 1 }));
    },
    [setPagination],
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      setPagination({ pageIndex: 0, pageSize: size });
    },
    [setPagination],
  );

  const activeFiltersCount = Object.keys(filters).length;

  return (
    <>
      <RemoteTableLayout
        table={table}
        toolbar={{
          columnVisibility,
          onColumnVisibilityChange: setColumnVisibility,
          availableColumns: AVAILABLE_COLUMNS,
          leftActions: (
            <FiltersButton
              activeFiltersCount={activeFiltersCount}
              isOpen={isFiltersOpen}
              onClick={() => setIsFiltersOpen((prev) => !prev)}
            />
          ),
          actions: (
            <Button
              variant="default"
              size="sm"
              onClick={() => setImportDialogOpen(true)}
            >
              <Download className="h-4 w-4 mr-1.5" />
              Import Job Postings
            </Button>
          ),
        }}
        filtersPane={
          isFiltersOpen ? (
            <JobPostingsFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
            />
          ) : undefined
        }
        tableContent={
          <JobPostingsTable
            table={table}
            isLoading={isLoading}
            data={postings}
            onRowClick={handleOpenDetail}
          />
        }
        pagination={{
          currentPage,
          totalPages,
          pageSize,
          totalCount,
          allowedPageSizes: ALLOWED_PAGE_SIZES,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
          itemLabel: "Job Posting(s)",
        }}
        error={error}
        isLoading={isLoading}
      />

      <JobPostingDetailDialog
        posting={selectedPosting}
        open={selectedPosting !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedPosting(null);
        }}
      />

      <ImportJobPostingsDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
      />
    </>
  );
}
